import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Border,
  Colors,
  type IconProps,
  Skeleton,
  classNames,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import { OpenAppButton } from '@/components/OpenAppButton';

import type { AppAccess } from '../types/home-types';

import { AppHeading } from './AppHeading';
import { LearnMoreButton } from './LearnMoreButton';

const CARD_HEIGHT = pxToRem(180);

const classes = createClasses({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('xs'),
    height: CARD_HEIGHT,
    padding: spacing('lg'),
    borderRadius: Border.Radius.Large,
    border: `1px solid ${Colors.Neutral.border}`,
    backgroundColor: Colors.Neutral.background,
    overflow: 'hidden',
    '@media (max-width: 720px)': {
      height: 'auto',
    },
  },
  disabled: {
    backgroundColor: Colors.Neutral.backgroundMedium,
  },
  skeleton: {
    height: CARD_HEIGHT,
    borderRadius: Border.Radius.Large,
  },
  top: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xs'),
  },
});

/**
 * The card an installed app shows on Home, a skeleton of its size while its
 * availability loads. Enabled, it links the app; disabled, it is muted with a
 * lock icon and a
 * "Learn more" button that opens an e-mail form about the app.
 */
export interface InstalledAppCardProps extends Partial<AppAccess> {
  icon: IconProps['icon'];
  iconColor: string;
  title: string;
  description: string;
  className?: string;
}

export const InstalledAppCard: React.FC<InstalledAppCardProps> = ({
  icon,
  iconColor,
  title,
  description,
  url,
  disabled = false,
  loading = false,
  className,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Skeleton
        className={classNames(classes.skeleton, className)}
        aria-busy="true"
        data-testid="app-card-skeleton"
      />
    );
  }

  return (
    <div
      className={classNames(
        classes.root,
        disabled && classes.disabled,
        className,
      )}
    >
      <AppHeading
        className={classes.top}
        icon={icon}
        iconColor={iconColor}
        title={title}
        description={description}
        locked={disabled}
      />
      <div className={classes.actions}>
        {!disabled && url ? (
          <OpenAppButton
            variant="primary"
            label={t('products.openApp')}
            url={url}
          />
        ) : (
          <LearnMoreButton appName={title} />
        )}
      </div>
    </div>
  );
};
